import { useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { COURAGES } from '@nextilt/commun';
import { api } from '../../partage/api/client';
import { cles } from '../../partage/relectures';
import type { CourageDeMaintenant, DossierDe, Ou, PossibleDuJour } from '../../partage/api/types';
import { FeuilleDuBas } from '../../partage/FeuilleDuBas';
import {
  ajouterALaJournee,
  boutonDeJeChoisis,
  CHERCHER_UNE_AUTRE_TACHE,
  CHERCHER_UNE_TACHE,
  MOTS_COURAGE,
  MOTS_CRANS_DE_TEMPS,
  nombreDePossibles,
  QUEL_COURAGE,
  QUEL_PROJET,
  QUELLE_DUREE,
  REPLIER_LA_RECHERCHE,
  RIEN_A_AJOUTER,
  RIEN_AVEC_CES_BOUTONS,
  RIEN_NE_CORRESPOND,
  SANS_PROJET,
  TOUS_LES_PROJETS,
} from '../../partage/vocabulaire';
import {
  CRANS_DE_TEMPS,
  filtresDeDepart,
  ligneAuChoix,
  projetsAuChoix,
  tachesAuChoix,
  tachesCherchees,
  type FiltresDuJour,
} from './filtresDuJour';
import { teinteDuProjet } from '../../apparence';
import './ListeAuChoix.css';

type Bouton = 'projet' | 'temps' | 'courage';
const TITRE_DE_LA_FEUILLE: Record<Bouton, string> = { projet: QUEL_PROJET, temps: QUELLE_DUREE, courage: QUEL_COURAGE };

const teinte = (d: DossierDe): CSSProperties => ({ ['--teinte' as string]: teinteDuProjet(d?.couleur) });

/**
 * La liste où l'on choisit ses tâches une à une, au téléphone (chantier `telephone-journee-agenda`,
 * ticket 03, décision d'Igor du 2026-10-05, 10 ; maquette `design/telephone.html`, piste
 * « Journée », « Je choisis ») : trois boutons côte à côte, le projet ▾, le temps ▾, le courage ▾ ;
 * chacun ouvre sa liste dans une feuille du bas, et le choix prend sa place. Dessous, les tâches qui
 * répondent, le débloquant en tête, chacune avec son + ; « Chercher une autre tâche » cherche dans
 * tout. « Je choisis moi-même » la montre sous la journée ; « + Ajouter une tâche » de
 * « Propose-moi » l'ouvre aussi.
 *
 * Les boutons restent tels quels d'un ajout à l'autre : on règle, on ajoute, on change pour la
 * tâche suivante, et ce qui est choisi reste dans la journée. Le courage relit les possibles du
 * jour à ce courage (le serveur range par la flottaison) ; « Fatigué » écarte le difficile.
 */
export function ListeAuChoix({
  jour,
  ou,
  courage,
  pris,
  reste,
  onAjouter,
}: {
  jour: string;
  ou: Ou;
  /** Le courage répondu à la question : celui du bouton au départ. */
  courage: CourageDeMaintenant;
  /** Les tâches déjà dans la journée. */
  pris: readonly string[];
  /** Le temps qui reste dans la journée, en minutes. */
  reste: number;
  onAjouter: (p: PossibleDuJour) => void;
}) {
  const [filtres, setFiltres] = useState<FiltresDuJour>(() => filtresDeDepart(courage));
  // Le projet choisi, pour son nom, même quand plus rien n'y passe.
  const [projetChoisi, setProjetChoisi] = useState<DossierDe>(null);
  const [feuille, setFeuille] = useState<Bouton | null>(null);
  const [recherche, setRecherche] = useState<string | null>(null);

  const preparation = useQuery({
    queryKey: cles.preparation(jour, ou, filtres.courage),
    queryFn: () => api.preparation(jour, ou, filtres.courage),
    placeholderData: (avant) => avant,
  });
  const possibles = preparation.data?.possibles ?? [];

  const taches = tachesAuChoix(possibles, filtres, pris, reste);
  const projets = projetsAuChoix(possibles, filtres, pris);
  if (filtres.projet !== null && !projets.some((p) => p.cle === filtres.projet)) {
    projets.push({ cle: filtres.projet, dossier: projetChoisi, combien: 0 });
  }
  const nomDuProjet = (cle: string | null, d: DossierDe) => (cle === null ? TOUS_LES_PROJETS : cle === '' ? SANS_PROJET : (d?.titre ?? SANS_PROJET));
  const combienAvec = (f: Partial<FiltresDuJour>) => tachesAuChoix(possibles, { ...filtres, ...f }, pris, reste).length;

  function choisir(f: Partial<FiltresDuJour>) {
    setFiltres((avant) => ({ ...avant, ...f }));
    setFeuille(null);
  }

  const boutons: { cle: Bouton; nom: 'Projet' | 'Temps' | 'Courage'; mot: string }[] = [
    { cle: 'projet', nom: 'Projet', mot: nomDuProjet(filtres.projet, projetChoisi) },
    { cle: 'temps', nom: 'Temps', mot: MOTS_CRANS_DE_TEMPS[filtres.temps] },
    { cle: 'courage', nom: 'Courage', mot: MOTS_COURAGE[filtres.courage] },
  ];

  const ligneDeLaFeuille = (cle: string, choisie: boolean, titre: string, combien: number, onChoisir: () => void, dossier?: DossierDe) => (
    <button
      key={cle}
      type="button"
      role="radio"
      aria-checked={choisie}
      className={`choisis-feuille-ligne${choisie ? ' est-choisie' : ''}`}
      data-feuille={cle}
      onClick={onChoisir}
    >
      {dossier !== undefined && <span className="choisis-pastille" style={teinte(dossier)} aria-hidden="true" />}
      <span className="choisis-feuille-mots">
        <b>{titre}</b>
        <small>{nombreDePossibles(combien)}</small>
      </span>
      <span className="choisis-feuille-coche" aria-hidden="true">
        {choisie ? '✓' : ''}
      </span>
    </button>
  );

  const ligneDeTache = (t: { tache: PossibleDuJour; tient: boolean; debloquant?: boolean }, avecProjet: boolean) => (
    <li
      key={t.tache.id}
      className={`preparer-ajout propose-ajout choisis-tache${t.tient ? '' : ' est-de-trop'}`}
      data-ajout={t.tache.id}
      data-tient={t.tient ? 'oui' : 'non'}
      data-debloquant={t.debloquant ? 'oui' : undefined}
    >
      <span className="preparer-quoi">
        <b>{t.tache.titre}</b> <small>· {ligneAuChoix({ debloquant: false, ...t }, avecProjet)}</small>
      </span>
      <button type="button" className="preparer-plus" aria-label={ajouterALaJournee(t.tache.titre)} onClick={() => onAjouter(t.tache)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
          <path d="M12 5v14M5 12h14" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      </button>
    </li>
  );

  const cherchees = recherche === null ? [] : tachesCherchees(possibles, recherche, pris, reste);

  return (
    <div className="choisis-liste" data-pret={preparation.data ? 'oui' : 'non'}>
      <div className="choisis-boutons">
        {boutons.map((b) => (
          <button
            key={b.cle}
            type="button"
            className="choisis-bouton"
            aria-haspopup="dialog"
            aria-expanded={feuille === b.cle}
            aria-label={boutonDeJeChoisis(b.nom, b.mot)}
            data-bouton={b.cle}
            onClick={() => setFeuille(b.cle)}
          >
            <span className="choisis-bouton-mot">{b.mot}</span>
            <span aria-hidden="true">▾</span>
          </button>
        ))}
      </div>

      {!preparation.data ? (
        <p className="propose-note">…</p>
      ) : possibles.length === 0 ? (
        <p className="propose-note">{RIEN_A_AJOUTER}</p>
      ) : taches.length === 0 ? (
        <p className="propose-note">{RIEN_AVEC_CES_BOUTONS}</p>
      ) : (
        <ul className="propose-ajouts choisis-taches">{taches.map((t) => ligneDeTache(t, filtres.projet === null))}</ul>
      )}

      <button
        type="button"
        className="propose-plus"
        aria-expanded={recherche !== null}
        data-geste="chercher"
        onClick={() => setRecherche(recherche === null ? '' : null)}
      >
        {recherche === null ? `${CHERCHER_UNE_AUTRE_TACHE} ›` : REPLIER_LA_RECHERCHE}
      </button>
      {recherche !== null && (
        <>
          <input
            type="search"
            className="preparer-champ choisis-champ"
            value={recherche}
            placeholder={CHERCHER_UNE_TACHE}
            aria-label={CHERCHER_UNE_TACHE}
            onChange={(e) => setRecherche(e.target.value)}
          />
          {cherchees.length === 0 ? (
            <p className="propose-note">{RIEN_NE_CORRESPOND}</p>
          ) : (
            <ul className="propose-ajouts choisis-cherchees">{cherchees.map((t) => ligneDeTache(t, true))}</ul>
          )}
        </>
      )}

      {feuille && (
        <FeuilleDuBas titre={TITRE_DE_LA_FEUILLE[feuille]} onFermer={() => setFeuille(null)}>
          <div className="choisis-feuille" role="radiogroup" aria-label={TITRE_DE_LA_FEUILLE[feuille]} data-feuille-de={feuille}>
            {feuille === 'projet' && [
              ligneDeLaFeuille('tous', filtres.projet === null, TOUS_LES_PROJETS, combienAvec({ projet: null }), () => choisir({ projet: null })),
              ...projets.map((p) =>
                ligneDeLaFeuille(
                  `projet-${p.cle}`,
                  filtres.projet === p.cle,
                  nomDuProjet(p.cle, p.dossier),
                  p.combien,
                  () => {
                    setProjetChoisi(p.dossier);
                    choisir({ projet: p.cle });
                  },
                  p.dossier
                )
              ),
            ]}
            {feuille === 'temps' &&
              CRANS_DE_TEMPS.map((c) =>
                ligneDeLaFeuille(`temps-${c}`, filtres.temps === c, MOTS_CRANS_DE_TEMPS[c], combienAvec({ temps: c }), () => choisir({ temps: c }))
              )}
            {feuille === 'courage' &&
              COURAGES.map((c) =>
                ligneDeLaFeuille(`courage-${c}`, filtres.courage === c, MOTS_COURAGE[c], combienAvec({ courage: c }), () => choisir({ courage: c }))
              )}
          </div>
        </FeuilleDuBas>
      )}
    </div>
  );
}
